/**
 * Micro-session storage helpers.
 *
 * The session ID is stored in two places:
 *  - localStorage  keyed by employee ID  → shared across browser tabs that open
 *                                           the same employee URL.
 *  - sessionStorage (single fixed key)   → a "current" mirror read by the global
 *                                           API constructor to inject the
 *                                           X-Micro-Session-Id header without
 *                                           needing to know the active employee ID.
 *
 * All direct storage access for micro-sessions should go through this module so
 * that callsites never hard-code storage keys or access patterns.
 */

const SESSION_STORAGE_KEY = 'micro_session_id';
const getLocalStorageKey = (employeeId: number) =>
  `micro_session_${employeeId}`;

/** Returns the session ID for a given employee, or null if none exists yet. */
export const getMicroSessionId = (employeeId: number): string | null =>
  localStorage.getItem(getLocalStorageKey(employeeId));

/**
 * Reads the current session ID from the sessionStorage mirror.
 * Used by the global API constructor which does not know the active employee ID.
 */
export const getCurrentMicroSessionId = (): string | null =>
  sessionStorage.getItem(SESSION_STORAGE_KEY);

/**
 * Persists a session ID for the given employee and mirrors it into sessionStorage
 * so the API constructor can inject the header on every request.
 */
export const setMicroSessionId = (
  employeeId: number,
  sessionId: string
): void => {
  localStorage.setItem(getLocalStorageKey(employeeId), sessionId);
  sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
};

/**
 * Clears the sessionStorage mirror only.
 * Called on component unmount — localStorage is intentionally left intact so that
 * other browser tabs viewing the same employee keep their session.
 */
export const clearCurrentMicroSession = (): void => {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

/**
 * Clears both the localStorage entry for the given employee and the sessionStorage
 * mirror. Use this on sign-out to ensure no stale session IDs remain.
 */
export const clearMicroSession = (employeeId: number): void => {
  localStorage.removeItem(getLocalStorageKey(employeeId));
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
};
